import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate, useRoute } from '@/ui/router';
import { Banner, CategoryChip, EmptyState } from '@/ui/components/bits';
import { generate, GENERATORS, generatorCategories } from '@/domain/generators';
import { instantiate } from '@/domain/template';
import { newSeed } from '@/domain/random';
import { checkAnswer, type CheckResult } from '@/domain/sigfigs';
import { recordAnswer } from '@/domain/practice';
import { db } from '@/db/db';
import type { GeneratedProblem } from '@/domain/problem';
import type { ProblemTemplate } from '@/domain/types';

/**
 * Randomised numeric practice. Built-in generators and imported templates land
 * here the same way, are graded the same way, and count towards progress and
 * the weak-spot dashboard the same way.
 */
export function ProblemsScreen() {
  const route = useRoute();
  const category = route.params.get('category') ?? undefined;
  /**
   * Specific generators, comma-separated. A category is too blunt for
   * revising one part of a paper: gas stoichiometry and partial pressures
   * are both "Gas Laws", and on the mid-term they are separate questions
   * worth eight marks and six.
   */
  const gen = route.params.get('gen') ?? undefined;
  /** What the caller is drilling, e.g. a part of the mid-term paper. */
  const title = route.params.get('title') ?? undefined;
  const { prefs, reload } = useAppState();

  const [templates, setTemplates] = useState<ProblemTemplate[]>([]);
  const [problem, setProblem] = useState<GeneratedProblem | null>(null);
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState<CheckResult | null>(null);
  const [showSteps, setShowSteps] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void db.templates.toArray().then((rows) => setTemplates(rows.filter((t) => !t.deleted)));
  }, []);

  const sources = useMemo(() => {
    const ids = gen ? new Set(gen.split(',').filter(Boolean)) : null;
    const builtIn = GENERATORS.filter(
      (g) => (ids ? ids.has(g.id) : true) && (!category || g.category === category),
    ).map((g) => ({
      kind: 'generator' as const,
      id: g.id,
      category: g.category,
    }));
    // Named generators mean a deliberate choice of question, so imported
    // templates are not quietly mixed in alongside them.
    const imported = ids
      ? []
      : templates
          .filter((t) => !category || t.category === category)
          .map((t) => ({ kind: 'template' as const, id: t.id, category: t.category }));
    return [...builtIn, ...imported];
  }, [templates, category, gen]);

  const draw = useCallback(() => {
    if (sources.length === 0) return;
    const source = sources[Math.floor(Math.random() * sources.length)]!;
    const seed = newSeed();
    try {
      const next =
        source.kind === 'generator'
          ? generate(source.id, seed)
          : instantiate(templates.find((t) => t.id === source.id)!, seed);
      setProblem(next);
      setTyped('');
      setResult(null);
      setShowSteps(false);
      setError(null);
    } catch (err) {
      setError(`That problem could not be built: ${(err as Error).message}`);
    }
  }, [sources, templates]);

  useEffect(() => {
    if (!problem && sources.length > 0) draw();
  }, [problem, sources, draw]);

  async function submit() {
    if (!problem || result) return;
    const checked = checkAnswer({
      typed,
      expectedValue: problem.answerValue,
      answerUnits: problem.answerUnits,
      unitAliases: problem.unitAliases,
      rule: problem.sigfigRule,
      ruleInputs: problem.ruleInputs,
      tolerance: problem.tolerance,
      mode: prefs?.sigfigMode ?? 'lenient',
    });
    setResult(checked);
    setShowSteps(true);

    // Problems count towards progress like anything else, keyed by their source.
    await recordAnswer({
      item: {
        id: problem.sourceId,
        category: problem.category,
        difficulty: problem.difficulty,
        course: 'CHE1045',
      },
      correct: checked.correct,
      mode: 'practice',
      seed: problem.seed,
    });
    await reload();
  }

  const categories = useMemo(() => {
    const fromTemplates = templates.map((t) => t.category);
    return [...new Set([...generatorCategories(), ...fromTemplates])].sort();
  }, [templates]);

  if (sources.length === 0) {
    return (
      <div className="stack">
        <h1>Practice problems</h1>
        <EmptyState icon="∑" title="Nothing for that topic">
          No generator or template covers {title ?? category}.
        </EmptyState>
        <button className="btn" onClick={() => navigate('/problems')}>
          All topics
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="spread">
        <h1>Practice problems</h1>
        <button className="btn btn--sm" onClick={() => navigate('/sigfig')}>
          Sig fig tool
        </button>
      </div>

      {title ? (
        <p className="faint" style={{ margin: 0 }}>
          Drilling <strong>{title}</strong>. Changing the topic below leaves the drill.
        </p>
      ) : null}

      <select
        className="select"
        value={category ?? 'all'}
        onChange={(e) => {
          const value = e.target.value;
          setProblem(null);
          navigate('/problems', value === 'all' ? {} : { category: value });
        }}
        aria-label="Topic"
      >
        <option value="all">Every topic</option>
        {categories.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      {error ? <Banner kind="error">{error}</Banner> : null}

      {problem ? (
        <div className="card stack">
          <div className="row">
            <CategoryChip category={problem.category} />
            <span className="chip chip--count">level {problem.difficulty}</span>
            {problem.sourceId.startsWith('gen-') ? null : (
              <span className="chip chip--count">from your notes</span>
            )}
          </div>

          <div className="question-text">{problem.prompt}</div>

          <label className="label" htmlFor="answer">
            Your answer{problem.answerUnits ? ` (in ${problem.answerUnits})` : ''}
          </label>
          <input
            id="answer"
            className="input mono"
            value={typed}
            disabled={Boolean(result)}
            placeholder={problem.answerUnits ? `e.g. 12.3 ${problem.answerUnits}` : 'e.g. 12.3'}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && typed.trim() && !result) void submit();
            }}
          />

          {!result ? (
            <div className="row">
              <button className="btn btn--primary" disabled={!typed.trim()} onClick={() => void submit()}>
                Check
              </button>
              <button className="btn" onClick={() => setShowSteps((s) => !s)}>
                {showSteps ? 'Hide steps' : 'Reveal steps'}
              </button>
              <button className="btn btn--ghost" onClick={draw}>
                Skip
              </button>
            </div>
          ) : (
            <>
              <div className={`banner banner--${result.correct ? 'ok' : 'error'}`}>
                <strong>{result.correct ? 'Correct' : 'Not quite'}</strong>
                <div>{result.message}</div>
              </div>
              {!result.valueOk ? (
                <div className="banner banner--info">
                  The answer is <span className="mono">{formatAnswer(problem)}</span>.
                </div>
              ) : null}
              <button className="btn btn--primary btn--block" onClick={draw}>
                Next problem
              </button>
            </>
          )}

          {showSteps && problem.steps.length > 0 ? (
            <>
              <h3 className="section-title" style={{ marginTop: 0 }}>
                How to do it
              </h3>
              <ol className="stack" style={{ paddingLeft: '1.2em', margin: 0 }}>
                {problem.steps.map((step, i) => (
                  <li key={i} style={{ whiteSpace: 'pre-wrap' }}>
                    {step}
                  </li>
                ))}
              </ol>
            </>
          ) : null}
        </div>
      ) : (
        <p className="muted">Drawing a problem…</p>
      )}

      <p className="faint" style={{ margin: 0 }}>
        Answers are graded {prefs?.sigfigMode === 'strict' ? 'strictly' : 'leniently'} on significant
        figures — change that in Settings. Units are always checked. Every problem is built from a
        seed, so the same one can be produced again.
      </p>
    </div>
  );
}

function formatAnswer(problem: GeneratedProblem): string {
  const magnitude = Math.abs(problem.answerValue);
  const text =
    magnitude !== 0 && (magnitude < 1e-3 || magnitude >= 1e6)
      ? problem.answerValue.toExponential(3)
      : String(Math.round(problem.answerValue * 1e6) / 1e6);
  return problem.answerUnits ? `${text} ${problem.answerUnits}` : text;
}
